import { trace } from '@opentelemetry/api';

/**
 * 현재 활성 OpenTelemetry 스팬의 W3C Trace Context 필드.
 * 서버: instrumentation.ts 에서 등록한 OTel provider 가 채워줌.
 * 클라이언트: web SDK 미설치 시 활성 스팬이 없어 빈 객체 반환(안전).
 */
export interface TraceFields {
  trace_id?: string;
  span_id?: string;
  /** W3C trace-flags (sampled 여부 등). 16진 2자리 문자열로 정규화 */
  trace_flags?: string;
}

export function activeTraceFields(): TraceFields {
  const span = trace.getActiveSpan();
  if (!span) {
    return {};
  }

  const ctx = span.spanContext();
  return {
    trace_id: ctx.traceId,
    span_id: ctx.spanId,
    trace_flags: ctx.traceFlags.toString(16).padStart(2, '0'),
  };
}

// W3C Trace Context 형식(소문자 hex 고정 길이). all-zero ID 는 스펙상 invalid.
const TRACE_ID_PATTERN = /^(?!0{32}$)[0-9a-f]{32}$/;
const SPAN_ID_PATTERN = /^(?!0{16}$)[0-9a-f]{16}$/;
const TRACE_FLAGS_PATTERN = /^[0-9a-f]{2}$/;

/**
 * 클라이언트에서 전송한 로그가 들고 온 trace 필드를 신뢰 가능한 형태로 추림.
 * 외부 공개 POST 채널(콘솔에서 임의 body 전송 가능)이므로 W3C 형식에 맞는
 * 값만 통과 — 임의 문자열이 ELK trace 필드에 주입되는 것을 차단한다.
 * (서버 route 에서 재-emit 할 때 사용)
 */
export function pickTraceFields(input: unknown): TraceFields {
  if (input === null || typeof input !== 'object') {
    return {};
  }

  const record = input as Record<string, unknown>;
  const result: TraceFields = {};

  if (
    typeof record.trace_id === 'string' &&
    TRACE_ID_PATTERN.test(record.trace_id)
  ) {
    result.trace_id = record.trace_id;
  }
  if (
    typeof record.span_id === 'string' &&
    SPAN_ID_PATTERN.test(record.span_id)
  ) {
    result.span_id = record.span_id;
  }
  if (
    typeof record.trace_flags === 'string' &&
    TRACE_FLAGS_PATTERN.test(record.trace_flags)
  ) {
    result.trace_flags = record.trace_flags;
  }

  return result;
}
